/**
 * LOGOI LAB - Alimento Sagrado: Sacred Food Recommendation Engine
 * Autonomous module for consciousness-aware food recommendations
 * 
 * Knowledge Sources:
 *   - Paper §5.4: 10 validated biochemical pathways (nutrient → dimension)
 *   - Paper §8.7: Inverse Alimentary Entropy (toxic food → ΔS_toxic)
 *   - ChefManny IngredientNeurochemicalMap: register/resonance concepts (adapted)
 *   - Perplexity research: 48 evidence-based food→neurotransmitter mappings
 *   - Traditions: Ayurveda, TCM, Mediterranean, Japanese, contemplative
 * 
 * Design Principle:
 *   LOGOS recommends WHAT to eat and WHY (based on consciousness state).
 *   ChefManny (if used separately) handles HOW to cook it.
 *   This module is 100% independent — zero ChefManny dependency.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState } from '../types';
import { clamp } from '../utils/math';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type Neurochemical = 'dopamine' | 'serotonin' | 'gaba' | 'oxytocin' | 'endorphin';

export type FlavorRegister = 'bass' | 'tenor' | 'alto' | 'soprano';

export type LogosDimension = 
  | 'peace' | 'sleep' | 'energy' | 'clarity' | 'focus' 
  | 'joy' | 'love' | 'wisdom' | 'gratitude' | 'presence'
  | 'gut_resonance' | 'nourishment' | 'taste_presence' | 'food_harmony';

export type EvidenceStrength = 'strong' | 'moderate' | 'emerging';

export type SacredTradition = 'ayurveda' | 'tcm' | 'mediterranean' | 'japanese' | 'contemplative' | 'universal';

export interface SacredFood {
  id: string;
  name: string;
  nameEs: string;
  neurochemicals: Partial<Record<Neurochemical, number>>;  // 0-10 intensity
  primaryDimension: LogosDimension;
  secondaryDimensions: LogosDimension[];
  register: FlavorRegister;
  resonance: number;        // 1-10: flavor vividness (promotes mindful eating)
  tradition: SacredTradition;
  evidence: EvidenceStrength;
  mechanism: string;
  mechanismEs: string;
  sacredNote: string;       // Why this food is "sacred" in LOGOS context
  sacredNoteEs: string;
}

export interface NutrientPathway {
  nutrient: string;
  dimension: LogosDimension;
  weight: number;           // 0-1 pathway weight from Paper §5.4
  mechanism: string;
  source: string;
}

export interface AlimentoRecommendation {
  deficientDimensions: { dimension: string; value: number; threshold: number }[];
  recommendedFoods: {
    food: SacredFood;
    reason: string;
    reasonEs: string;
    predictedChange: number;  // Δ dimension
  }[];
  avoidFoods: string[];
  avoidFoodsEs: string[];
  generalAdvice: string;
  generalAdviceEs: string;
}

export interface MealHarmonyScore {
  food_harmony: number;       // 0-1: register balance
  taste_presence: number;     // 0-1: resonance average
  registerDistribution: Record<FlavorRegister, number>;
  isHarmonious: boolean;
  advice: string;
  adviceEs: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// 10 BIOCHEMICAL PATHWAYS (Paper §5.4 — Validated)
// ═══════════════════════════════════════════════════════════════════════════

export const NUTRIENT_PATHWAYS: NutrientPathway[] = [
  { nutrient: 'magnesium', dimension: 'peace', weight: 0.15, mechanism: 'GABA receptor modulation', source: 'Boyle, Lawton and Dye (2017)' },
  { nutrient: 'omega3_dha', dimension: 'clarity', weight: 0.12, mechanism: 'Synaptic membrane fluidity', source: 'Gómez-Pinilla (2008)' },
  { nutrient: 'iron', dimension: 'energy', weight: 0.12, mechanism: 'Haemoglobin oxygen transport', source: 'WHO (2020)' },
  { nutrient: 'tryptophan', dimension: 'sleep', weight: 0.10, mechanism: 'Serotonin/melatonin precursor', source: 'Silber and Schmitt (2010)' },
  { nutrient: 'choline', dimension: 'clarity', weight: 0.10, mechanism: 'Acetylcholine synthesis', source: 'Zeisel (2006)' },
  { nutrient: 'b_vitamins', dimension: 'energy', weight: 0.08, mechanism: 'Mitochondrial ATP production', source: 'Kennedy (2016)' },
  { nutrient: 'vitamin_d', dimension: 'joy', weight: 0.08, mechanism: 'Serotonin receptor expression', source: 'Penckofer et al. (2010)' },
  { nutrient: 'zinc', dimension: 'focus', weight: 0.07, mechanism: 'Glutamatergic neurotransmission', source: 'Szewczyk et al. (2011)' },
  { nutrient: 'probiotics', dimension: 'gut_resonance', weight: 0.15, mechanism: 'Microbiome-gut-brain axis', source: 'Bell et al. (2023)' },
  { nutrient: 'polyphenols', dimension: 'wisdom', weight: 0.05, mechanism: 'Neuroprotection, BDNF upregulation', source: 'Rendeiro et al. (2015)' },
];

// ═══════════════════════════════════════════════════════════════════════════
// SACRED FOOD DATABASE (~50 foods)
// Sources: Perplexity research + ChefManny neurochemical profiles (adapted)
// Organized by primary consciousness dimension
// ═══════════════════════════════════════════════════════════════════════════

export const SACRED_FOODS: SacredFood[] = [

  // ── PEACE / CALM (serotonin + GABA pathway) ──────────────────────────

  {
    id: 'green_tea', name: 'Green Tea', nameEs: 'Té Verde',
    neurochemicals: { gaba: 8, serotonin: 7, dopamine: 3 },
    primaryDimension: 'peace', secondaryDimensions: ['clarity', 'presence'],
    register: 'alto', resonance: 6, tradition: 'japanese', evidence: 'strong',
    mechanism: 'L-theanine boosts GABA and serotonin, promoting alpha brain waves',
    mechanismEs: 'L-teanina eleva GABA y serotonina, promoviendo ondas alfa cerebrales',
    sacredNote: 'The Japanese tea ceremony is meditation in liquid form — presence through every sip',
    sacredNoteEs: 'La ceremonia del té es meditación en forma líquida — presencia en cada sorbo',
  },
  {
    id: 'mushroom', name: 'Mushrooms (Shiitake, Reishi)', nameEs: 'Hongos (Shiitake, Reishi)',
    neurochemicals: { serotonin: 7, gaba: 6, dopamine: 4 },
    primaryDimension: 'peace', secondaryDimensions: ['gut_resonance', 'wisdom'],
    register: 'bass', resonance: 8, tradition: 'japanese', evidence: 'moderate',
    mechanism: 'Beta-glucans support microbiome; GABA precursors promote calm',
    mechanismEs: 'Beta-glucanos apoyan microbioma; precursores GABA promueven calma',
    sacredNote: 'Forest meditation on a plate — earthy grounding that reconnects with nature',
    sacredNoteEs: 'Meditación del bosque en un plato — arraigo terrenal que reconecta con la naturaleza',
  },
  {
    id: 'bone_broth', name: 'Bone Broth', nameEs: 'Caldo de Hueso',
    neurochemicals: { serotonin: 8, gaba: 8, oxytocin: 6 },
    primaryDimension: 'peace', secondaryDimensions: ['gut_resonance', 'nourishment'],
    register: 'bass', resonance: 5, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Glycine activates GABA receptors; collagen repairs gut lining',
    mechanismEs: 'Glicina activa receptores GABA; colágeno repara mucosa intestinal',
    sacredNote: 'Ancestral healing — slow time transforms bones into medicine',
    sacredNoteEs: 'Sanación ancestral — el tiempo lento transforma huesos en medicina',
  },
  {
    id: 'banana', name: 'Banana', nameEs: 'Plátano',
    neurochemicals: { serotonin: 6, gaba: 5, dopamine: 4 },
    primaryDimension: 'peace', secondaryDimensions: ['energy', 'sleep'],
    register: 'tenor', resonance: 4, tradition: 'ayurveda', evidence: 'moderate',
    mechanism: 'Tryptophan precursor to serotonin; prebiotic fiber feeds GABA-producing bacteria',
    mechanismEs: 'Triptófano precursor de serotonina; fibra prebiótica alimenta bacterias productoras de GABA',
    sacredNote: 'Gentle sweetness that calms without stimulating — ayurvedic sattvic food',
    sacredNoteEs: 'Dulzura gentil que calma sin estimular — alimento sáttvico ayurvédico',
  },
  {
    id: 'oats', name: 'Oats', nameEs: 'Avena',
    neurochemicals: { serotonin: 6, gaba: 5 },
    primaryDimension: 'peace', secondaryDimensions: ['sleep', 'energy'],
    register: 'tenor', resonance: 3, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Tryptophan-rich; complex carbs sustain serotonin production',
    mechanismEs: 'Rica en triptófano; carbohidratos complejos sostienen producción de serotonina',
    sacredNote: 'Morning ritual food — starts the day with grounded calm',
    sacredNoteEs: 'Alimento del ritual matutino — inicia el día con calma arraigada',
  },
  {
    id: 'spinach', name: 'Spinach', nameEs: 'Espinaca',
    neurochemicals: { gaba: 7, serotonin: 4 },
    primaryDimension: 'peace', secondaryDimensions: ['energy', 'clarity'],
    register: 'alto', resonance: 4, tradition: 'mediterranean', evidence: 'moderate',
    mechanism: 'Glutamate precursor to GABA; rich in magnesium for GABA receptor modulation',
    mechanismEs: 'Glutamato precursor de GABA; rica en magnesio para modulación de receptores GABA',
    sacredNote: 'Green vitality that calms the nervous system while nourishing the blood',
    sacredNoteEs: 'Vitalidad verde que calma el sistema nervioso mientras nutre la sangre',
  },
  {
    id: 'miso', name: 'Miso', nameEs: 'Miso',
    neurochemicals: { gaba: 7, serotonin: 6, oxytocin: 4 },
    primaryDimension: 'peace', secondaryDimensions: ['gut_resonance', 'nourishment'],
    register: 'bass', resonance: 8, tradition: 'japanese', evidence: 'moderate',
    mechanism: 'Fermentation produces GABA directly; umami activates vagal pathways',
    mechanismEs: 'Fermentación produce GABA directamente; umami activa vías vagales',
    sacredNote: 'Japanese wisdom: a bowl of miso is a daily prayer to the gut-brain axis',
    sacredNoteEs: 'Sabiduría japonesa: un bowl de miso es oración diaria al eje intestino-cerebro',
  },

  // ── CLARITY / FOCUS (dopamine + norepinephrine pathway) ──────────────

  {
    id: 'dark_chocolate', name: 'Dark Chocolate (70%+)', nameEs: 'Chocolate Oscuro (70%+)',
    neurochemicals: { dopamine: 9, serotonin: 7, endorphin: 8, oxytocin: 4 },
    primaryDimension: 'clarity', secondaryDimensions: ['joy', 'focus', 'presence'],
    register: 'soprano', resonance: 9, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Flavanols boost dopamine/norepinephrine via sensory brain signaling; theobromine enhances focus',
    mechanismEs: 'Flavanoles elevan dopamina/norepinefrina vía señalización sensorial; teobromina mejora enfoque',
    sacredNote: 'The Aztecs called it "food of the gods" — consciousness expansion through taste',
    sacredNoteEs: 'Los aztecas lo llamaban "alimento de los dioses" — expansión de consciencia por sabor',
  },
  {
    id: 'blueberry', name: 'Blueberries', nameEs: 'Arándanos',
    neurochemicals: { dopamine: 6, serotonin: 4 },
    primaryDimension: 'clarity', secondaryDimensions: ['wisdom', 'focus'],
    register: 'soprano', resonance: 6, tradition: 'universal', evidence: 'strong',
    mechanism: 'Anthocyanins cross BBB, boost BDNF and dopamine in hippocampus',
    mechanismEs: 'Antocianinas cruzan BHE, elevan BDNF y dopamina en hipocampo',
    sacredNote: 'Brain food — neuroprotection that preserves the clarity needed for wisdom',
    sacredNoteEs: 'Alimento cerebral — neuroprotección que preserva la claridad necesaria para sabiduría',
  },
  {
    id: 'egg', name: 'Eggs', nameEs: 'Huevos',
    neurochemicals: { serotonin: 5, dopamine: 5, oxytocin: 5 },
    primaryDimension: 'clarity', secondaryDimensions: ['energy', 'focus'],
    register: 'tenor', resonance: 5, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Choline for acetylcholine synthesis; tyrosine for dopamine; complete amino acid profile',
    mechanismEs: 'Colina para síntesis de acetilcolina; tirosina para dopamina; perfil aminoácido completo',
    sacredNote: 'The perfect nutritional package — symbol of potential and wholeness',
    sacredNoteEs: 'El paquete nutricional perfecto — símbolo de potencial y completitud',
  },
  {
    id: 'olive_oil', name: 'Extra Virgin Olive Oil', nameEs: 'Aceite de Oliva Extra Virgen',
    neurochemicals: { dopamine: 5, serotonin: 4, endorphin: 3 },
    primaryDimension: 'clarity', secondaryDimensions: ['wisdom', 'gut_resonance'],
    register: 'tenor', resonance: 6, tradition: 'mediterranean', evidence: 'strong',
    mechanism: 'Monounsaturated fats support synaptic plasticity; polyphenols are neuroprotective',
    mechanismEs: 'Grasas monoinsaturadas apoyan plasticidad sináptica; polifenoles son neuroprotectores',
    sacredNote: 'Mediterranean liquid gold — anointing oil of civilizations, now validated by neuroscience',
    sacredNoteEs: 'Oro líquido mediterráneo — aceite de unción de civilizaciones, ahora validado por neurociencia',
  },

  // ── ENERGY (dopamine + B-vitamins + iron pathway) ────────────────────

  {
    id: 'salmon', name: 'Wild Salmon', nameEs: 'Salmón Salvaje',
    neurochemicals: { dopamine: 7, serotonin: 6, endorphin: 4 },
    primaryDimension: 'energy', secondaryDimensions: ['clarity', 'joy', 'focus'],
    register: 'tenor', resonance: 7, tradition: 'japanese', evidence: 'strong',
    mechanism: 'Omega-3 DHA enhances synaptic plasticity and dopamine signaling; rich in B12',
    mechanismEs: 'Omega-3 DHA mejora plasticidad sináptica y señalización dopaminérgica; rico en B12',
    sacredNote: 'The fish that swims upstream — energy directed by purpose, not impulse',
    sacredNoteEs: 'El pez que nada contra corriente — energía dirigida por propósito, no impulso',
  },
  {
    id: 'avocado', name: 'Avocado', nameEs: 'Aguacate',
    neurochemicals: { dopamine: 6, serotonin: 4 },
    primaryDimension: 'energy', secondaryDimensions: ['clarity', 'nourishment'],
    register: 'tenor', resonance: 5, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Tyrosine and healthy fats support dopamine uptake (vs saturated fats which block it)',
    mechanismEs: 'Tirosina y grasas saludables apoyan captación de dopamina (vs grasas saturadas que la bloquean)',
    sacredNote: 'Ancient Mesoamerican fertility fruit — sustained energy without the crash',
    sacredNoteEs: 'Fruta de fertilidad mesoamericana — energía sostenida sin el bajón',
  },
  {
    id: 'walnut', name: 'Walnuts', nameEs: 'Nueces',
    neurochemicals: { dopamine: 5, serotonin: 5, endorphin: 3 },
    primaryDimension: 'energy', secondaryDimensions: ['clarity', 'wisdom'],
    register: 'tenor', resonance: 6, tradition: 'mediterranean', evidence: 'moderate',
    mechanism: 'ALA omega-3 converts to DHA; shape resembles brain (doctrine of signatures)',
    mechanismEs: 'ALA omega-3 se convierte en DHA; su forma recuerda al cerebro (doctrina de signaturas)',
    sacredNote: 'Brain-shaped for a reason — Mediterranean monks ate them for mental clarity',
    sacredNoteEs: 'Forma de cerebro por una razón — monjes mediterráneos las comían para claridad mental',
  },
  {
    id: 'lentils', name: 'Lentils', nameEs: 'Lentejas',
    neurochemicals: { dopamine: 5, serotonin: 5 },
    primaryDimension: 'energy', secondaryDimensions: ['joy', 'nourishment'],
    register: 'tenor', resonance: 4, tradition: 'ayurveda', evidence: 'moderate',
    mechanism: 'Tyrosine and tryptophan precursors; iron for oxygen transport; B-vitamins for ATP',
    mechanismEs: 'Precursores de tirosina y triptófano; hierro para transporte de oxígeno; vitaminas B para ATP',
    sacredNote: 'Humility on a plate — the most nourishing food is often the simplest',
    sacredNoteEs: 'Humildad en un plato — el alimento más nutritivo suele ser el más simple',
  },
  {
    id: 'ginger', name: 'Ginger', nameEs: 'Jengibre',
    neurochemicals: { dopamine: 5, serotonin: 4, endorphin: 4 },
    primaryDimension: 'energy', secondaryDimensions: ['gut_resonance', 'presence'],
    register: 'alto', resonance: 7, tradition: 'ayurveda', evidence: 'emerging',
    mechanism: 'Enhances digestion and dopamine via gut; gingerols are anti-inflammatory',
    mechanismEs: 'Mejora digestión y dopamina vía intestino; gingeroles son antiinflamatorios',
    sacredNote: 'Ayurvedic fire — awakens digestive agni and clears energetic stagnation',
    sacredNoteEs: 'Fuego ayurvédico — despierta el agni digestivo y limpia estancamiento energético',
  },

  // ── SLEEP (serotonin → melatonin pathway) ────────────────────────────

  {
    id: 'cherry', name: 'Tart Cherries', nameEs: 'Cerezas Ácidas',
    neurochemicals: { serotonin: 7, gaba: 4 },
    primaryDimension: 'sleep', secondaryDimensions: ['peace'],
    register: 'soprano', resonance: 6, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Natural melatonin from tryptophan conversion; anthocyanins reduce inflammation',
    mechanismEs: 'Melatonina natural de conversión de triptófano; antocianinas reducen inflamación',
    sacredNote: 'Nature\'s sleeping pill — evidence-based melatonin in fruit form',
    sacredNoteEs: 'La pastilla para dormir de la naturaleza — melatonina basada en evidencia en forma de fruta',
  },
  {
    id: 'chamomile', name: 'Chamomile Tea', nameEs: 'Té de Manzanilla',
    neurochemicals: { gaba: 7, serotonin: 5 },
    primaryDimension: 'sleep', secondaryDimensions: ['peace', 'presence'],
    register: 'soprano', resonance: 5, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Apigenin binds GABA-A receptors (same site as benzodiazepines, but gentle)',
    mechanismEs: 'Apigenina se une a receptores GABA-A (mismo sitio que benzodiacepinas, pero gentil)',
    sacredNote: 'Evening ritual — the bridge between waking consciousness and surrender to sleep',
    sacredNoteEs: 'Ritual vespertino — el puente entre consciencia vigil y entrega al sueño',
  },
  {
    id: 'turkey', name: 'Turkey', nameEs: 'Pavo',
    neurochemicals: { serotonin: 7, dopamine: 4 },
    primaryDimension: 'sleep', secondaryDimensions: ['peace', 'nourishment'],
    register: 'tenor', resonance: 4, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Highest tryptophan content per serving; converts to serotonin then melatonin',
    mechanismEs: 'Mayor contenido de triptófano por porción; se convierte en serotonina y luego melatonina',
    sacredNote: 'The gratitude bird — traditionally eaten in communal thanksgiving',
    sacredNoteEs: 'El ave de la gratitud — tradicionalmente comida en acción de gracias comunitaria',
  },

  // ── JOY / MOOD (serotonin + endorphin + gut-brain pathway) ───────────

  {
    id: 'kimchi', name: 'Kimchi', nameEs: 'Kimchi',
    neurochemicals: { serotonin: 7, gaba: 6 },
    primaryDimension: 'joy', secondaryDimensions: ['gut_resonance', 'energy'],
    register: 'alto', resonance: 8, tradition: 'japanese', evidence: 'moderate',
    mechanism: 'Probiotics boost gut serotonin (95% of body serotonin is in gut); capsaicin releases endorphins',
    mechanismEs: 'Probióticos elevan serotonina intestinal (95% de serotonina corporal está en intestino); capsaicina libera endorfinas',
    sacredNote: 'Living food — billions of beneficial bacteria transforming vegetables into joy',
    sacredNoteEs: 'Alimento vivo — miles de millones de bacterias benéficas transformando vegetales en alegría',
  },
  {
    id: 'kefir', name: 'Kefir', nameEs: 'Kéfir',
    neurochemicals: { gaba: 7, serotonin: 6, oxytocin: 4 },
    primaryDimension: 'joy', secondaryDimensions: ['peace', 'gut_resonance'],
    register: 'tenor', resonance: 5, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Diverse probiotics enhance GABA synthesis via gut-brain axis',
    mechanismEs: 'Probióticos diversos mejoran síntesis de GABA vía eje intestino-cerebro',
    sacredNote: 'Caucasus mountain elixir — fermented milk that has sustained communities for millennia',
    sacredNoteEs: 'Elixir del Cáucaso — leche fermentada que ha sostenido comunidades por milenios',
  },
  {
    id: 'chili', name: 'Chili Peppers', nameEs: 'Chiles',
    neurochemicals: { endorphin: 9, dopamine: 8, serotonin: 3 },
    primaryDimension: 'joy', secondaryDimensions: ['energy', 'presence'],
    register: 'alto', resonance: 9, tradition: 'universal', evidence: 'strong',
    mechanism: 'Capsaicin triggers pain receptors → endorphin release → euphoria ("chili high")',
    mechanismEs: 'Capsaicina activa receptores de dolor → liberación de endorfinas → euforia',
    sacredNote: 'Sacred fire of Mesoamerica — transforms pain into bliss, the alchemical principle',
    sacredNoteEs: 'Fuego sagrado de Mesoamérica — transforma dolor en dicha, el principio alquímico',
  },
  {
    id: 'honey', name: 'Raw Honey', nameEs: 'Miel Cruda',
    neurochemicals: { serotonin: 7, dopamine: 5, oxytocin: 5 },
    primaryDimension: 'joy', secondaryDimensions: ['energy', 'sleep'],
    register: 'soprano', resonance: 7, tradition: 'ayurveda', evidence: 'moderate',
    mechanism: 'Natural sugars boost serotonin; enzymatic compounds support gut health',
    mechanismEs: 'Azúcares naturales elevan serotonina; compuestos enzimáticos apoyan salud intestinal',
    sacredNote: 'In every tradition — manna, amrita, nectar — honey is the taste of grace',
    sacredNoteEs: 'En toda tradición — maná, amrita, néctar — la miel es el sabor de la gracia',
  },
  {
    id: 'mango', name: 'Mango', nameEs: 'Mango',
    neurochemicals: { dopamine: 7, serotonin: 5, endorphin: 4 },
    primaryDimension: 'joy', secondaryDimensions: ['energy', 'presence'],
    register: 'soprano', resonance: 8, tradition: 'ayurveda', evidence: 'emerging',
    mechanism: 'High tyrosine for dopamine; beta-carotene; sensory pleasure triggers reward circuits',
    mechanismEs: 'Alta tirosina para dopamina; beta-caroteno; placer sensorial activa circuitos de recompensa',
    sacredNote: 'The king of fruits in Ayurveda — pure sattvic joy in every bite',
    sacredNoteEs: 'El rey de las frutas en Ayurveda — alegría sáttvica pura en cada bocado',
  },

  // ── GUT RESONANCE (probiotic + prebiotic + vagal pathway) ────────────

  {
    id: 'sauerkraut', name: 'Sauerkraut', nameEs: 'Chucrut',
    neurochemicals: { gaba: 7, serotonin: 5 },
    primaryDimension: 'gut_resonance', secondaryDimensions: ['peace', 'joy'],
    register: 'alto', resonance: 6, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Probiotic GABA production via Lactobacillus; fiber feeds beneficial microbiome',
    mechanismEs: 'Producción probiótica de GABA vía Lactobacillus; fibra alimenta microbioma benéfico',
    sacredNote: 'Transformation through patience — cabbage becomes medicine through time and salt',
    sacredNoteEs: 'Transformación por paciencia — el repollo se vuelve medicina con tiempo y sal',
  },
  {
    id: 'turmeric', name: 'Turmeric', nameEs: 'Cúrcuma',
    neurochemicals: { serotonin: 5, gaba: 4, endorphin: 3 },
    primaryDimension: 'gut_resonance', secondaryDimensions: ['peace', 'clarity'],
    register: 'alto', resonance: 7, tradition: 'ayurveda', evidence: 'moderate',
    mechanism: 'Curcumin modulates gut inflammation; enhances BDNF; supports serotonin/GABA balance',
    mechanismEs: 'Curcumina modula inflamación intestinal; mejora BDNF; apoya balance serotonina/GABA',
    sacredNote: 'Ayurvedic gold — sacred in Hindu ceremonies, now validated as anti-inflammatory',
    sacredNoteEs: 'Oro ayurvédico — sagrada en ceremonias hindúes, ahora validada como antiinflamatoria',
  },
  {
    id: 'yogurt', name: 'Natural Yogurt', nameEs: 'Yogur Natural',
    neurochemicals: { gaba: 6, serotonin: 5, oxytocin: 4 },
    primaryDimension: 'gut_resonance', secondaryDimensions: ['peace', 'nourishment'],
    register: 'tenor', resonance: 4, tradition: 'mediterranean', evidence: 'strong',
    mechanism: 'Lactobacillus produces GABA via fermentation; calcium supports vagal signaling',
    mechanismEs: 'Lactobacillus produce GABA por fermentación; calcio apoya señalización vagal',
    sacredNote: 'The oldest probiotic — cultures that eat cultured food have cultured microbiomes',
    sacredNoteEs: 'El probiótico más antiguo — culturas que comen alimento cultivado tienen microbiomas cultivados',
  },
  {
    id: 'garlic', name: 'Garlic', nameEs: 'Ajo',
    neurochemicals: { serotonin: 4, gaba: 4, endorphin: 3 },
    primaryDimension: 'gut_resonance', secondaryDimensions: ['energy', 'nourishment'],
    register: 'alto', resonance: 8, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Prebiotic inulin feeds beneficial bacteria; allicin is antimicrobial against pathogens',
    mechanismEs: 'Inulina prebiótica alimenta bacterias benéficas; alicina es antimicrobiana contra patógenos',
    sacredNote: 'Universal protector — every culture uses garlic to ward off illness (and vampires)',
    sacredNoteEs: 'Protector universal — toda cultura usa ajo para alejar enfermedad (y vampiros)',
  },
  {
    id: 'ghee', name: 'Ghee (Clarified Butter)', nameEs: 'Ghee (Mantequilla Clarificada)',
    neurochemicals: { endorphin: 5, oxytocin: 4, serotonin: 3 },
    primaryDimension: 'gut_resonance', secondaryDimensions: ['nourishment', 'peace'],
    register: 'tenor', resonance: 5, tradition: 'ayurveda', evidence: 'emerging',
    mechanism: 'Butyrate from fermentation supports gut barrier integrity and oxytocin release',
    mechanismEs: 'Butirato de fermentación apoya integridad de barrera intestinal y liberación de oxitocina',
    sacredNote: 'Sacred in Vedic tradition — clarified like consciousness itself, pure and luminous',
    sacredNoteEs: 'Sagrado en tradición védica — clarificado como la consciencia misma, puro y luminoso',
  },

  // ── LOVE / COMPASSION (oxytocin pathway) ─────────────────────────────

  {
    id: 'cacao', name: 'Raw Cacao', nameEs: 'Cacao Crudo',
    neurochemicals: { oxytocin: 7, dopamine: 8, endorphin: 7, serotonin: 5 },
    primaryDimension: 'love', secondaryDimensions: ['joy', 'presence', 'clarity'],
    register: 'soprano', resonance: 10, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Anandamide ("bliss molecule") + PEA (phenylethylamine, "love chemical") + theobromine',
    mechanismEs: 'Anandamida ("molécula de la dicha") + PEA (feniletilamina, "químico del amor") + teobromina',
    sacredNote: 'Cacao ceremonies open the heart — the highest-resonance food in the sacred database',
    sacredNoteEs: 'Las ceremonias de cacao abren el corazón — el alimento de mayor resonancia en la base sagrada',
  },
  {
    id: 'rose', name: 'Rose Water / Petals', nameEs: 'Agua de Rosa / Pétalos',
    neurochemicals: { oxytocin: 8, serotonin: 6, dopamine: 4 },
    primaryDimension: 'love', secondaryDimensions: ['peace', 'presence', 'gratitude'],
    register: 'soprano', resonance: 7, tradition: 'contemplative', evidence: 'emerging',
    mechanism: 'Aroma activates limbic system; associated with love across all cultures',
    mechanismEs: 'Aroma activa sistema límbico; asociada con amor en todas las culturas',
    sacredNote: 'Sufi poets drink rose water — the flower of the heart chakra, love made edible',
    sacredNoteEs: 'Poetas sufíes beben agua de rosa — la flor del chakra cardíaco, amor hecho comestible',
  },

  // ── WISDOM / NEUROPROTECTION (polyphenol + BDNF pathway) ─────────────

  {
    id: 'pomegranate', name: 'Pomegranate', nameEs: 'Granada',
    neurochemicals: { dopamine: 5, serotonin: 4, endorphin: 4 },
    primaryDimension: 'wisdom', secondaryDimensions: ['clarity', 'energy'],
    register: 'soprano', resonance: 7, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Punicalagins are potent neuroprotective polyphenols; urolithins from gut conversion enhance BDNF',
    mechanismEs: 'Punicalaginas son polifenoles neuroprotectores potentes; urolitinas de conversión intestinal mejoran BDNF',
    sacredNote: 'Symbol of wisdom in Greek, Jewish, and Islamic traditions — each seed a facet of truth',
    sacredNoteEs: 'Símbolo de sabiduría en tradiciones griega, judía e islámica — cada semilla una faceta de la verdad',
  },
  {
    id: 'green_tea_matcha', name: 'Matcha', nameEs: 'Matcha',
    neurochemicals: { gaba: 7, dopamine: 6, serotonin: 5 },
    primaryDimension: 'wisdom', secondaryDimensions: ['clarity', 'presence', 'peace'],
    register: 'alto', resonance: 8, tradition: 'japanese', evidence: 'strong',
    mechanism: 'Concentrated L-theanine + EGCG; promotes alpha waves (alert calm); catechins are neuroprotective',
    mechanismEs: 'L-teanina concentrada + EGCG; promueve ondas alfa (calma alerta); catequinas son neuroprotectoras',
    sacredNote: 'Zen monks drink matcha before meditation — alert presence without agitation',
    sacredNoteEs: 'Monjes zen beben matcha antes de meditar — presencia alerta sin agitación',
  },

  // ── PRESENCE / MINDFULNESS (sensory awareness pathway) ───────────────

  {
    id: 'vanilla', name: 'Vanilla', nameEs: 'Vainilla',
    neurochemicals: { serotonin: 8, oxytocin: 7, dopamine: 5 },
    primaryDimension: 'presence', secondaryDimensions: ['peace', 'love', 'joy'],
    register: 'soprano', resonance: 7, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Vanillin activates olfactory-limbic pathway; associated with comfort and safety',
    mechanismEs: 'Vanilina activa vía olfatoria-límbica; asociada con confort y seguridad',
    sacredNote: 'The Totonac people believed vanilla was born from a forbidden love — pure devotion made fragrance',
    sacredNoteEs: 'Los totonacas creían que la vainilla nació de un amor prohibido — devoción pura hecha fragancia',
  },
  {
    id: 'cinnamon', name: 'Cinnamon', nameEs: 'Canela',
    neurochemicals: { serotonin: 7, oxytocin: 5, dopamine: 4 },
    primaryDimension: 'presence', secondaryDimensions: ['peace', 'energy'],
    register: 'alto', resonance: 7, tradition: 'ayurveda', evidence: 'moderate',
    mechanism: 'Cinnamaldehyde activates TRPA1 receptors, heightening sensory awareness',
    mechanismEs: 'Cinamaldehído activa receptores TRPA1, intensificando conciencia sensorial',
    sacredNote: 'One of the ancient sacred spices — traded as precious as gold, awakens all senses',
    sacredNoteEs: 'Una de las especias sagradas antiguas — comerciada preciosa como oro, despierta todos los sentidos',
  },
  {
    id: 'lemon', name: 'Lemon', nameEs: 'Limón',
    neurochemicals: { dopamine: 6, serotonin: 4, endorphin: 5 },
    primaryDimension: 'presence', secondaryDimensions: ['energy', 'clarity'],
    register: 'alto', resonance: 7, tradition: 'universal', evidence: 'moderate',
    mechanism: 'Citric acid stimulates trigeminal nerve; limonene activates alertness pathways',
    mechanismEs: 'Ácido cítrico estimula nervio trigémino; limoneno activa vías de alerta',
    sacredNote: 'Morning lemon water is a worldwide ritual — the acid cuts through mental fog',
    sacredNoteEs: 'Agua con limón matutina es ritual mundial — el ácido corta la niebla mental',
  },

  // ── GRATITUDE (oxytocin + communal pathway) ──────────────────────────

  {
    id: 'bread', name: 'Artisan Bread', nameEs: 'Pan Artesanal',
    neurochemicals: { serotonin: 5, oxytocin: 6, gaba: 3 },
    primaryDimension: 'gratitude', secondaryDimensions: ['peace', 'nourishment', 'love'],
    register: 'tenor', resonance: 6, tradition: 'contemplative', evidence: 'emerging',
    mechanism: 'Complex carbs sustain serotonin; communal breaking of bread triggers oxytocin',
    mechanismEs: 'Carbohidratos complejos sostienen serotonina; compartir pan dispara oxitocina',
    sacredNote: '"Give us this day our daily bread" — the most universal sacred food across all traditions',
    sacredNoteEs: '"Danos hoy nuestro pan de cada día" — el alimento sagrado más universal en todas las tradiciones',
  },
  {
    id: 'wine_red', name: 'Red Wine (moderate)', nameEs: 'Vino Tinto (moderado)',
    neurochemicals: { dopamine: 5, gaba: 5, endorphin: 4, oxytocin: 4 },
    primaryDimension: 'gratitude', secondaryDimensions: ['joy', 'peace', 'presence'],
    register: 'bass', resonance: 9, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Resveratrol is neuroprotective; ritual consumption triggers social bonding (oxytocin)',
    mechanismEs: 'Resveratrol es neuroprotector; consumo ritual dispara vinculación social (oxitocina)',
    sacredNote: 'Sacramental in Christianity, kiddush in Judaism — consciousness shifts through sacred intention, not quantity',
    sacredNoteEs: 'Sacramental en cristianismo, kidush en judaísmo — la consciencia cambia por intención sagrada, no cantidad',
  },

  // ── NOURISHMENT (complete nutrition pathway) ─────────────────────────

  {
    id: 'quinoa', name: 'Quinoa', nameEs: 'Quinoa',
    neurochemicals: { serotonin: 5, dopamine: 4, gaba: 3 },
    primaryDimension: 'nourishment', secondaryDimensions: ['energy', 'peace'],
    register: 'tenor', resonance: 4, tradition: 'contemplative', evidence: 'moderate',
    mechanism: 'Complete protein (all 9 essential amino acids); magnesium for GABA; iron for energy',
    mechanismEs: 'Proteína completa (9 aminoácidos esenciales); magnesio para GABA; hierro para energía',
    sacredNote: 'Inca "mother grain" — chisaya mama, sustenance that the earth gives freely',
    sacredNoteEs: 'Grano madre inca — chisaya mama, sustento que la tierra da libremente',
  },
  {
    id: 'seaweed', name: 'Seaweed (Nori, Kombu)', nameEs: 'Algas (Nori, Kombu)',
    neurochemicals: { gaba: 5, serotonin: 4 },
    primaryDimension: 'nourishment', secondaryDimensions: ['energy', 'gut_resonance'],
    register: 'alto', resonance: 6, tradition: 'japanese', evidence: 'emerging',
    mechanism: 'Iodine supports thyroid/NT balance; minerals from the ocean; natural glutamate for umami',
    mechanismEs: 'Yodo apoya balance tiroideo/NT; minerales del océano; glutamato natural para umami',
    sacredNote: 'The ocean\'s gift — minerals and wisdom from the primordial source of life',
    sacredNoteEs: 'Regalo del océano — minerales y sabiduría de la fuente primordial de vida',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// DIMENSION → DEFICIENCY THRESHOLDS
// ═══════════════════════════════════════════════════════════════════════════

const DEFICIENCY_THRESHOLDS: Partial<Record<string, number>> = {
  peace: 0.40, sleep: 0.35, energy: 0.40, clarity: 0.40,
  focus: 0.40, joy: 0.40, love: 0.45, wisdom: 0.40,
  gratitude: 0.40, presence: 0.40, creativity: 0.40,
  nutrition: 0.40, // current v2 name for nourishment
};

// ═══════════════════════════════════════════════════════════════════════════
// TOXIC FOOD INDICATORS (Paper §8.7 — Inverse Alimentary Entropy)
// ═══════════════════════════════════════════════════════════════════════════

const TOXIC_FOOD_WARNINGS = {
  ultraProcessed: {
    label: 'Ultra-processed foods (NOVA-4)',
    labelEs: 'Alimentos ultraprocesados (NOVA-4)',
    examples: 'sodas, chips, instant noodles, frozen dinners, candy',
    examplesEs: 'refrescos, papas fritas, sopas instantáneas, comida congelada, dulces',
    affectedDimensions: ['energy', 'peace', 'clarity', 'sleep', 'gut_resonance'] as string[],
    mechanism: 'Microbiome dysbiosis → neuroinflammation → HPA dysregulation → vagal suppression',
  },
  refinedSugar: {
    label: 'Refined sugar (>25g/day)',
    labelEs: 'Azúcar refinada (>25g/día)',
    examples: 'pastries, soda, candy, sweetened cereals',
    examplesEs: 'pastelería, refrescos, dulces, cereales azucarados',
    affectedDimensions: ['peace', 'sleep', 'energy', 'focus'] as string[],
    mechanism: 'Cortisol spikes → HPA axis dysregulation → chronic stress',
  },
  transFats: {
    label: 'Trans fats and seed oils',
    labelEs: 'Grasas trans y aceites de semilla',
    examples: 'margarine, fried fast food, packaged baked goods',
    examplesEs: 'margarina, comida rápida frita, productos de panadería empacados',
    affectedDimensions: ['clarity', 'focus', 'joy', 'energy'] as string[],
    mechanism: 'TNF-α, IL-6 cross BBB → neuroinflammation → cognitive degradation',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// RECOMMENDATION ENGINE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get sacred food recommendations based on current consciousness state.
 * Identifies deficient dimensions and recommends foods that address them.
 */
export function getAlimentoRecommendations(state: ConsciousnessState): AlimentoRecommendation {
  const deficient: { dimension: string; value: number; threshold: number }[] = [];

  for (const [dim, threshold] of Object.entries(DEFICIENCY_THRESHOLDS)) {
    if (threshold === undefined) continue;
    const value = (state as Record<string, number>)[dim];
    if (value !== undefined && value < threshold) {
      deficient.push({ dimension: dim, value, threshold });
    }
  }

  deficient.sort((a, b) => a.value - b.value);

  const recommended: AlimentoRecommendation['recommendedFoods'] = [];
  const usedIds = new Set<string>();

  for (const def of deficient.slice(0, 3)) {
    const dimKey = def.dimension === 'nutrition' ? 'nourishment' : def.dimension;

    const matching = SACRED_FOODS
      .filter(f => !usedIds.has(f.id))
      .filter(f => 
        f.primaryDimension === dimKey || 
        f.secondaryDimensions.includes(dimKey as LogosDimension)
      )
      .sort((a, b) => {
        const aScore = (a.primaryDimension === dimKey ? 2 : 1) * a.resonance;
        const bScore = (b.primaryDimension === dimKey ? 2 : 1) * b.resonance;
        return bScore - aScore;
      })
      .slice(0, 2);

    for (const food of matching) {
      usedIds.add(food.id);
      const gap = def.threshold - def.value;
      const predictedChange = Math.min(0.10, gap * 0.3 * (food.resonance / 10));

      recommended.push({
        food,
        reason: `Your ${def.dimension} is at ${(def.value * 100).toFixed(0)}%. ${food.name} supports ${def.dimension} via ${food.mechanism}`,
        reasonEs: `Tu ${def.dimension} está al ${(def.value * 100).toFixed(0)}%. ${food.nameEs} apoya ${def.dimension} vía ${food.mechanismEs}`,
        predictedChange,
      });
    }
  }

  const generalAdvice = deficient.length === 0
    ? 'Your consciousness state is balanced. Maintain variety across all four registers (bass, tenor, alto, soprano) for sustained harmony.'
    : `Focus on ${deficient[0].dimension} today. One mindful meal with the recommended foods can shift your trajectory toward A⁺.`;

  const generalAdviceEs = deficient.length === 0
    ? 'Tu estado de consciencia está equilibrado. Mantén variedad en los cuatro registros (bajo, tenor, alto, soprano) para armonía sostenida.'
    : `Enfócate en ${deficient[0].dimension} hoy. Una comida consciente con los alimentos recomendados puede mover tu trayectoria hacia A⁺.`;

  return {
    deficientDimensions: deficient,
    recommendedFoods: recommended,
    avoidFoods: ['Ultra-processed foods', 'Refined sugar > 25g', 'Trans fats', 'Seed oils'],
    avoidFoodsEs: ['Alimentos ultraprocesados', 'Azúcar refinada > 25g', 'Grasas trans', 'Aceites de semilla'],
    generalAdvice,
    generalAdviceEs,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// MEAL HARMONY ASSESSMENT
// Uses register distribution concept (adapted from ChefManny musical DB)
// Evidence: Meal variety in flavors/textures affects vagal tone beyond nutrition
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Assess the harmonic balance of a meal based on its ingredient registers.
 * A meal spanning all 4 registers (bass→soprano) scores higher food_harmony.
 */
export function assessMealHarmony(foodIds: string[]): MealHarmonyScore {
  const foods = foodIds
    .map(id => SACRED_FOODS.find(f => f.id === id))
    .filter((f): f is SacredFood => f !== undefined);

  if (foods.length === 0) {
    return {
      food_harmony: 0.3,
      taste_presence: 0.3,
      registerDistribution: { bass: 0, tenor: 0, alto: 0, soprano: 0 },
      isHarmonious: false,
      advice: 'No recognized sacred foods in the meal. Try adding at least one food from the sacred database.',
      adviceEs: 'No se reconocieron alimentos sagrados en la comida. Intenta agregar al menos uno de la base sagrada.',
    };
  }

  const dist: Record<FlavorRegister, number> = { bass: 0, tenor: 0, alto: 0, soprano: 0 };
  let totalResonance = 0;

  for (const food of foods) {
    dist[food.register]++;
    totalResonance += food.resonance;
  }

  const registersPresent = Object.values(dist).filter(v => v > 0).length;
  const registerBalance = registersPresent / 4;
  const avgResonance = totalResonance / foods.length;

  const food_harmony = clamp(registerBalance * 0.6 + (avgResonance / 10) * 0.4);
  const taste_presence = clamp(avgResonance / 10);
  const isHarmonious = registersPresent >= 3;

  const missingRegisters = Object.entries(dist)
    .filter(([_, count]) => count === 0)
    .map(([reg]) => reg);

  let advice = isHarmonious
    ? `Excellent harmonic balance — ${registersPresent}/4 registers present.`
    : `Add foods from missing registers: ${missingRegisters.join(', ')} for fuller harmony.`;

  let adviceEs = isHarmonious
    ? `Excelente balance armónico — ${registersPresent}/4 registros presentes.`
    : `Agrega alimentos de registros faltantes: ${missingRegisters.join(', ')} para armonía más completa.`;

  return { food_harmony, taste_presence, registerDistribution: dist, isHarmonious, advice, adviceEs };
}

// ═══════════════════════════════════════════════════════════════════════════
// DIMENSION CHANGE PREDICTION (Paper §5.4)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Predict consciousness dimension changes from consuming given foods.
 * Uses validated biochemical pathways from Paper §5.4.
 */
export function predictDimensionChanges(foodIds: string[]): Record<string, number> {
  const changes: Record<string, number> = {};

  const foods = foodIds
    .map(id => SACRED_FOODS.find(f => f.id === id))
    .filter((f): f is SacredFood => f !== undefined);

  for (const food of foods) {
    const dims = [food.primaryDimension, ...food.secondaryDimensions];
    for (let i = 0; i < dims.length; i++) {
      const dim = dims[i];
      const weight = i === 0 ? 0.06 : 0.02;
      const effect = weight * (food.resonance / 10);
      changes[dim] = (changes[dim] || 0) + effect;
    }
  }

  for (const key of Object.keys(changes)) {
    changes[key] = Math.min(0.15, changes[key]);
  }

  return changes;
}

// ═══════════════════════════════════════════════════════════════════════════
// INVERSE ENTROPY ASSESSMENT (Paper §8.7)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Estimate the entropic impact of toxic food consumption.
 * ΔS_toxic = Σ δ_p · ε_p · (1 − Λ)
 */
export function estimateEntropicDamage(
  toxicIntensity: number,   // 0-1: how "processed" the meal is
  lambda: number            // current Λ alignment
): {
  deltaSEntropy: number;
  vulnerability: number;
  warning: string;
  warningEs: string;
} {
  const vulnerability = 1 - clamp(lambda);
  const deltaSEntropy = clamp(toxicIntensity * vulnerability);

  let warning = '';
  let warningEs = '';

  if (deltaSEntropy > 0.10) {
    warning = `High entropic damage (ΔS=${(deltaSEntropy * 100).toFixed(0)}%). Your low alignment (Λ=${(lambda * 100).toFixed(0)}%) makes you vulnerable. Prioritize sacred foods to rebuild alignment.`;
    warningEs = `Daño entrópico alto (ΔS=${(deltaSEntropy * 100).toFixed(0)}%). Tu baja alineación (Λ=${(lambda * 100).toFixed(0)}%) te hace vulnerable. Prioriza alimentos sagrados para reconstruir alineación.`;
  } else if (deltaSEntropy > 0.05) {
    warning = `Moderate entropic impact. Your alignment provides partial resilience but this meal is working against your trajectory.`;
    warningEs = `Impacto entrópico moderado. Tu alineación provee resiliencia parcial pero esta comida trabaja contra tu trayectoria.`;
  } else {
    warning = `Low entropic impact. Your alignment (Λ=${(lambda * 100).toFixed(0)}%) provides strong resilience against dietary entropy.`;
    warningEs = `Impacto entrópico bajo. Tu alineación (Λ=${(lambda * 100).toFixed(0)}%) provee fuerte resiliencia contra entropía dietética.`;
  }

  return { deltaSEntropy, vulnerability, warning, warningEs };
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ═══════════════════════════════════════════════════════════════════════════

export { TOXIC_FOOD_WARNINGS };
